import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { apiFetch } from "../../../api";
import { AiChatProvider } from "../learning/AiChatContext";
import AiChatPopup from "../learning/components/AiChatPopup";
import { AiSparkleIcon } from "../learning/components/AiChatPopup";
import SentenceReviewCard from "./SentenceReviewCard";
import s from "./SentenceBoard.module.css";
const DIR_KEY = "sentenceBoard.direction";
const STREAK_KEY = "sentenceBoard.streak";
const DAILY_KEY = "sentenceBoard.daily";
// const DAILY_GOAL = 5;

function readDir() {
  try { return localStorage.getItem(DIR_KEY) || "en_to_sv"; } catch { return "en_to_sv"; }
}
function writeDir(val) {
  try { localStorage.setItem(DIR_KEY, val); } catch {}
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function readStreak() {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (!raw) return { count: 0, lastDate: "" };
    return JSON.parse(raw);
  } catch { return { count: 0, lastDate: "" }; }
}

function updateStreak() {
  const today = todayStr();
  const s = readStreak();
  if (s.lastDate === today) return s;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yStr = yesterday.toISOString().slice(0, 10);
  const next = { count: s.lastDate === yStr ? s.count + 1 : 1, lastDate: today };
  try { localStorage.setItem(STREAK_KEY, JSON.stringify(next)); } catch {}
  return next;
}

function readDaily() {
  try {
    const raw = localStorage.getItem(DAILY_KEY);
    if (!raw) return { date: todayStr(), count: 0 };
    const d = JSON.parse(raw);
    return d.date === todayStr() ? d : { date: todayStr(), count: 0 };
  } catch { return { date: todayStr(), count: 0 }; }
}

function incrementDaily() {
  const d = readDaily();
  const next = { date: todayStr(), count: d.count + 1 };
  try { localStorage.setItem(DAILY_KEY, JSON.stringify(next)); } catch {}
  return next;
}

const LEVELS = [
  { min: 0, label: "Nyborjare", en: "Beginner" },
  { min: 5, label: "Upptackare", en: "Explorer" },
  { min: 15, label: "Samlare", en: "Collector" },
  { min: 30, label: "Kannare", en: "Expert" },
  { min: 50, label: "Mastare", en: "Master" },
];

function getLevel(totalSaved) {  // eslint-disable-line no-unused-vars
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (totalSaved >= LEVELS[i].min) return { ...LEVELS[i], index: i };
  }
  return { ...LEVELS[0], index: 0 };
}

// function nextLevelProgress(totalSaved) {
//   const lvl = getLevel(totalSaved);
//   const nextIdx = lvl.index + 1;
//   if (nextIdx >= LEVELS.length) return 1;
//   const current = LEVELS[lvl.index].min;
//   const next = LEVELS[nextIdx].min;
//   return (totalSaved - current) / (next - current);
// }

function DailyRing({ count, goal }) {  // eslint-disable-line no-unused-vars
  const pct = Math.min(count / goal, 1);
  const r = 18;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct);
  const done = pct >= 1;
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" className={`${s.dailyRing} ${done ? s.dailyRingDone : ""}`}>
      <circle cx="22" cy="22" r={r} fill="none" stroke="rgba(212,168,67,0.12)" strokeWidth="3" />
      <circle
        cx="22" cy="22" r={r} fill="none"
        stroke={done ? "#2d6a4f" : "#d4a843"}
        strokeWidth="3" strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={offset}
        transform="rotate(-90 22 22)"
        className={s.dailyRingProgress}
      />
      <text x="22" y="23" textAnchor="middle" dominantBaseline="central"
        className={s.dailyRingText} fill={done ? "#2d6a4f" : "#b8701a"}>
        {count}
      </text>
    </svg>
  );
}

function PlayIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
    </svg>
  );
}

function TrashIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
  );
}

const META_CLS = { "new": "metaNew", mastered: "metaMastered", due: "metaDue", soon: "metaSoon", later: "metaLater" };

function formatDueInfo(sent) {
  if (sent.is_mastered) return { text: "Mastered", cls: "mastered" };
  if (!sent.next_review_at || sent.total_reviews === 0) return { text: "New", cls: "new" };
  const diffDays = Math.ceil((new Date(sent.next_review_at).getTime() - Date.now()) / 86400000);
  if (diffDays <= 0) return { text: "Due now", cls: "due" };
  if (diffDays === 1) return { text: "Tomorrow", cls: "soon" };
  return { text: `In ${diffDays} days`, cls: "later" };
}

export default function SentenceBoard() {
  const [direction, setDirection] = useState(readDir);
  const [inputText, setInputText] = useState("");
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);

  const [sentences, setSentences] = useState([]);
  const [dueCount, setDueCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [dueSentences, setDueSentences] = useState([]);

  const [showAiChat, setShowAiChat] = useState(false);
  const [moduleContext, setModuleContext] = useState(null);
  const [aiSentenceCtx, setAiSentenceCtx] = useState(null);

  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const [deletingId, setDeletingId] = useState(null);
  const [activeTab, setActiveTab] = useState("translate");
  const [reviewState, setReviewState] = useState(null);

  const [streak, setStreak] = useState(readStreak);   // eslint-disable-line no-unused-vars
  const [daily, setDaily] = useState(readDaily);       // eslint-disable-line no-unused-vars
  const [saveFlash, setSaveFlash] = useState(false);

  const aiChatValue = useMemo(() => ({ setModuleContext }), []);

  // Fetch sentences list
  const fetchSentences = useCallback(async () => {
    try {
      const res = await apiFetch("/api/v1/sentences");
      if (!res.ok) throw new Error("Failed to load sentences");
      const data = await res.json();
      setSentences(data.sentences || []);
      setDueCount(data.due_count || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSentences(); }, [fetchSentences]);

  // Direction toggle
  const handleToggleDirection = useCallback(() => {
    setDirection((d) => {
      const next = d === "en_to_sv" ? "sv_to_en" : "en_to_sv";
      writeDir(next);
      return next;
    });
    setInputText("");
    setTranslation(null);
  }, []);

  // Translate
  const handleTranslate = useCallback(async () => {
    if (!inputText.trim() || translating) return;
    setTranslating(true);
    setTranslation(null);
    setError("");
    try {
      const res = await apiFetch("/api/v1/sentences/translate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: inputText.trim(), direction }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Translation failed");
      }
      const data = await res.json();
      setTranslation(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setTranslating(false);
    }
  }, [inputText, direction, translating]);

  // Save
  const handleSave = useCallback(async () => {
    if (!translation || saving) return;
    setSaving(true);
    try {
      const res = await apiFetch("/api/v1/sentences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          in_swedish: translation.in_swedish,
          in_english: translation.in_english,
          direction,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to save");
      }
      setInputText("");
      setTranslation(null);
      fetchSentences();
      // gamification updates
      setStreak(updateStreak());
      setDaily(incrementDaily());
      setSaveFlash(true);
      setTimeout(() => setSaveFlash(false), 1200);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [translation, saving, direction, fetchSentences]);

  // Delete
  const handleDelete = useCallback(async (id) => {
    if (deletingId) return;
    setDeletingId(id);
    try {
      await apiFetch(`/api/v1/sentences/${id}`, { method: "DELETE" });
      setSentences((prev) => prev.filter((s) => s.id !== id));
      setDueCount((c) => Math.max(0, c - 1));
    } catch {
      // silent
    } finally {
      setDeletingId(null);
    }
  }, [deletingId]);

  // Play audio
  const handlePlay = useCallback((sentenceId) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (playingId === sentenceId) { setPlayingId(null); return; }
    setPlayingId(sentenceId);
    const audio = new Audio();
    apiFetch(`/api/v1/sentences/${sentenceId}/audio`).then(async (res) => {
      if (res.redirected) {
        audio.src = res.url;
      } else {
        const blob = await res.blob();
        audio.src = URL.createObjectURL(blob);
      }
      audio.play().catch(() => {});
    }).catch(() => setPlayingId(null));
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => setPlayingId(null);
    audioRef.current = audio;
  }, [playingId]);

  // Cleanup audio
  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
  }, []);

  // Start review
  const handleStartReview = useCallback(async () => {
    try {
      const res = await apiFetch("/api/v1/sentences/due");
      if (!res.ok) throw new Error("Failed to fetch due sentences");
      const data = await res.json();
      setDueSentences(data);
      setActiveTab("due");
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // End review
  const handleReviewComplete = useCallback(() => {
    setDueSentences([]);
    setActiveTab("saved");
    fetchSentences();
  }, [fetchSentences]);

  // Ask AI about a sentence
  const handleAskAi = useCallback((sentence) => {
    setAiSentenceCtx(sentence);
    setShowAiChat(true);
  }, []);

  const isEnToSv = direction === "en_to_sv";

  return (
    <div className={s.panel}>
      {/* Header */}
      <div className={s.header}>
        <div className={s.headerIcon}>
          <svg className={s.headerIconSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3M12.334 5.364a48.11 48.11 0 0 1 3.413.263M15.747 5.627a40.42 40.42 0 0 1-3.413-.263m0 0A42.18 42.18 0 0 0 9 5.25m3.334.114A49.5 49.5 0 0 1 15 9.75" />
          </svg>
        </div>
        <h2 className={`${s.headerTitle} ${activeTab === "due" && reviewState?.status === "playing" ? s.headerTitleCompact : ""}`}>Sentences</h2>
        {activeTab === "due" && reviewState?.status === "playing" && (
          <div className={s.headerProgress}>
            <div className={s.headerProgressFill} style={{ width: `${((reviewState.currentIndex + 1) / reviewState.total) * 100}%` }} />
          </div>
        )}
        {activeTab === "due" && (
          <button className={s.headerAiBtn} onClick={() => setShowAiChat(true)} aria-label="Ask AI">
            <AiSparkleIcon className={s.headerAiBtnIcon} />
          </button>
        )}
      </div>

      <div className={s.tabBar}>
        <button
          className={`${s.tab} ${activeTab === "translate" ? s.tabActive : ""}`}
          onClick={() => setActiveTab("translate")}
        >
          Translate
        </button>
        <button
          className={`${s.tab} ${activeTab === "due" ? s.tabActive : ""} ${dueCount > 0 && activeTab !== "due" ? s.tabDue : ""}`}
          onClick={dueCount > 0 ? handleStartReview : undefined}
          disabled={dueCount === 0}
        >
          Due
          {dueCount > 0 && <span className={s.tabDueCount}>{dueCount}</span>}
        </button>
        <button
          className={`${s.tab} ${activeTab === "saved" ? s.tabActive : ""}`}
          onClick={() => setActiveTab("saved")}
        >
          Saved
          {sentences.length > 0 && <span className={s.tabCount}>({sentences.length})</span>}
        </button>
      </div>

      {activeTab === "due" ? (
        <div className={s.dueContent}>
          <AiChatProvider value={aiChatValue}>
            <SentenceReviewCard
              sentences={dueSentences}
              onBack={() => { setActiveTab("saved"); fetchSentences(); }}
              onComplete={handleReviewComplete}
              onAskAi={() => setShowAiChat(true)}
              embedded
              onStateChange={setReviewState}
            />
          </AiChatProvider>
        </div>
      ) : (
      <div className={s.content}>
        {activeTab === "translate" ? (
          <>
          <div className={s.translateBar}>
            <div className={s.directionRow}>
              <button
                className={`${s.dirBtn} ${isEnToSv ? s.dirBtnActive : ""}`}
                onClick={() => { if (!isEnToSv) handleToggleDirection(); }}
              >
                EN → SV
              </button>
              <button
                className={`${s.dirBtn} ${!isEnToSv ? s.dirBtnActive : ""}`}
                onClick={() => { if (isEnToSv) handleToggleDirection(); }}
              >
                SV → EN
              </button>
            </div>

            <textarea
              className={s.input}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={isEnToSv ? "Type in English..." : "Skriv på svenska..."}
              rows={2}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleTranslate(); }
              }}
            />

            {translation && (
              <div className={s.translationResult}>
                <div className={s.translationLang}>
                  <span className={s.langTag}>SV</span>
                  <span className={`${s.translationText} ${s.translationTextSv}`}>{translation.in_swedish}</span>
                  <button
                    className={s.ttsBtn}
                    onClick={() => {
                      apiFetch("/api/v1/sentences/tts", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ text: translation.in_swedish }),
                      }).then(res => res.blob()).then(blob => {
                        const a = new Audio(URL.createObjectURL(blob));
                        a.play().catch(() => {});
                      }).catch(() => {});
                    }}
                    title="Listen in Swedish"
                  >
                    <PlayIcon className={s.ttsBtnIcon} />
                  </button>
                </div>
                <div className={s.translationLang}>
                  <span className={s.langTag}>EN</span>
                  <span className={s.translationText}>{translation.in_english}</span>
                </div>
              </div>
            )}

            {error && <p className={s.errorMsg}>{error}</p>}

            <div className={s.translateActions}>
              <button
                className={s.translateBtn}
                onClick={handleTranslate}
                disabled={!inputText.trim() || translating}
              >
                {translating ? "Translating..." : "Translate"}
              </button>
              {translation && (
                <div className={s.saveBtnWrap}>
                  <button
                    className={s.saveBtn}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save to Collection"}
                  </button>
                  {saveFlash && <span className={s.saveParticle}>+1</span>}
                </div>
              )}
            </div>
          </div>
          </>
        ) : loading ? (
          <div className={s.emptyState}>
            <div className={s.spinner} />
            <p>Loading sentences...</p>
          </div>
        ) : sentences.length === 0 ? (
          <div className={s.emptyState}>
            <svg className={s.emptyIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3M12.334 5.364a48.11 48.11 0 0 1 3.413.263" />
            </svg>
            <p className={s.emptyText}>No phrases yet</p>
            <button className={s.emptyAction} onClick={() => setActiveTab("translate")}>
              Translate your first sentence
            </button>
          </div>
        ) : (
          <div className={s.sentenceList}>
            {sentences.map((sent) => (
              <div key={sent.id} className={s.sentenceCard}>
                <div className={s.sentenceContent}>
                  <p className={s.sentenceSv}>{sent.in_swedish}</p>
                  <p className={s.sentenceEn}>{sent.in_english}</p>
                  {(() => {
                    const info = formatDueInfo(sent);
                    return (
                      <div className={s.sentenceMeta}>
                        <span className={`${s.metaTag} ${s[META_CLS[info.cls]]}`}>{info.text}</span>
                        {sent.total_reviews > 0 && (
                          <span className={s.metaStats}>
                            {sent.total_reviews} review{sent.total_reviews !== 1 ? "s" : ""} · {Math.round((sent.total_correct / sent.total_reviews) * 100)}%
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className={s.sentenceActions}>
                  <button
                    className={`${s.iconBtn} ${playingId === sent.id ? s.iconBtnActive : ""}`}
                    onClick={() => handlePlay(sent.id)}
                    title="Play Swedish audio"
                  >
                    <PlayIcon className={s.iconBtnSvg} />
                  </button>
                  <button
                    className={s.iconBtn}
                    onClick={() => handleAskAi(sent)}
                    title="Ask AI"
                  >
                    <AiSparkleIcon className={s.iconBtnSvg} />
                  </button>
                  <button
                    className={`${s.iconBtn} ${s.iconBtnDanger}`}
                    onClick={() => handleDelete(sent.id)}
                    disabled={deletingId === sent.id}
                    title="Delete"
                  >
                    <TrashIcon className={s.iconBtnSvg} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {showAiChat && (
        <AiChatPopup
          onClose={() => { setShowAiChat(false); setAiSentenceCtx(null); }}
          context={
            activeTab === "due"
              ? (moduleContext ?? { origin: "sentence_review" })
              : {
                  origin: "sentence_board",
                  ...(aiSentenceCtx ? {
                    sentence: {
                      in_swedish: aiSentenceCtx.in_swedish,
                      in_english: aiSentenceCtx.in_english,
                    },
                  } : {}),
                }
          }
        />
      )}
    </div>
  );
}
